import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  GitBranch, 
  Plus, 
  Edit2, 
  Trash2, 
  Building2,
  MapPin,
  Package,
  MoreVertical,
  AlertCircle,
  Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const getConfidenceBadge = (level) => {
  const variants = {
    high: 'bg-green-100 text-green-700 border-green-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-red-100 text-red-700 border-red-200'
  };
  return (
    <Badge variant="outline" className={variants[level] || variants.medium}>
      {level}
    </Badge>
  );
};

const SegmentRuleManagement = () => {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [filters, setFilters] = useState({
    oem: '',
    competitor_id: '',
    part_type_id: '',
    region: ''
  });
  const [formData, setFormData] = useState({
    competitor_id: '',
    oem: '',
    part_type_id: '',
    region: '',
    estimated_share_percent: '',
    confidence_level: 'medium',
    source_notes: ''
  });

  // Fetch segment rules
  const { data: segmentRules = [], isLoading } = useQuery({
    queryKey: ['segmentRules', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.oem && filters.oem !== 'all') params.append('oem', filters.oem);
      if (filters.competitor_id && filters.competitor_id !== 'all') params.append('competitor_id', filters.competitor_id);
      if (filters.part_type_id && filters.part_type_id !== 'all') params.append('part_type_id', filters.part_type_id);
      if (filters.region && filters.region !== 'all') params.append('region', filters.region);
      if (filters.region) params.append('region', filters.region);
      const response = await apiClient.get(`/api/competitive-intelligence/segment-rules?${params}`);
      return response.data;
    }
  });

  // Fetch competitors for dropdown
  const { data: competitors = [] } = useQuery({
    queryKey: ['competitors'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/competitors');
      return response.data;
    }
  });

  // Fetch part types for dropdown
  const { data: partTypes = [] } = useQuery({
    queryKey: ['partTypes'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/part-types');
      return response.data;
    }
  });

  // Fetch available OEMs
  const { data: oemsData } = useQuery({
    queryKey: ['availableOems'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-oems');
      return response.data;
    }
  });
  const oems = oemsData?.oems || [];

  // Fetch available regions
  const { data: regionsData } = useQuery({
    queryKey: ['availableRegions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-regions');
      return response.data;
    }
  });
  const regions = regionsData?.regions || [];

  // Create segment rule mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('/api/competitive-intelligence/segment-rules', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentRules']);
      setIsCreateDialogOpen(false);
      resetForm();
    }
  });

  // Update segment rule mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`/api/competitive-intelligence/segment-rules/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentRules']);
      setEditingRule(null);
      resetForm();
    }
  });

  // Delete segment rule mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`/api/competitive-intelligence/segment-rules/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['segmentRules']);
    }
  });

  const resetForm = () => {
    setFormData({
      competitor_id: '',
      oem: '',
      part_type_id: '',
      region: '',
      estimated_share_percent: '',
      confidence_level: 'medium',
      source_notes: ''
    });
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      competitor_id: rule.competitor_id || '',
      oem: rule.oem || '',
      part_type_id: rule.part_type_id || '',
      region: rule.region || '',
      estimated_share_percent: rule.estimated_share_percent || '',
      confidence_level: rule.confidence_level || 'medium',
      source_notes: rule.source_notes || ''
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      estimated_share_percent: formData.estimated_share_percent ? parseFloat(formData.estimated_share_percent) : null,
      part_type_id: (formData.part_type_id && formData.part_type_id !== 'all') ? formData.part_type_id : null,
      region: (formData.region && formData.region !== 'all') ? formData.region : null
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingRule(null);
    resetForm();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Segment Rules</h2>
          <p className="text-sm text-slate-500 mt-1">
            Define default competitor assignments by OEM, Part Type, and Region
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} disabled={competitors.length === 0}>
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {/* Warning if no competitors */}
      {competitors.length === 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-sm text-amber-800">
                Please add competitors first before creating segment rules.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select 
              value={filters.competitor_id} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, competitor_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Competitors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Competitors</SelectItem>
                {competitors.map((comp) => (
                  <SelectItem key={comp.id} value={comp.id}>
                    {comp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.oem} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, oem: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All OEMs" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all">All OEMs</SelectItem>
                {oems.map((oem) => (
                  <SelectItem key={oem} value={oem}>
                    {oem}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.part_type_id} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, part_type_id: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Part Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Part Types</SelectItem>
                {partTypes.map((pt) => (
                  <SelectItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.region} 
              onValueChange={(value) => setFilters(prev => ({ ...prev, region: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading segment rules...</div>
      ) : segmentRules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No segment rules defined</h3>
            <p className="text-slate-500 mb-4">
              Create rules to assign competitors to OEM/Part/Region segments
            </p>
            {competitors.length > 0 && (
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Competitor</TableHead>
                <TableHead>OEM</TableHead>
                <TableHead>Part Type</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">Share %</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {segmentRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span className="font-medium">{rule.competitor_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{rule.oem}</TableCell>
                  <TableCell>
                    {rule.part_type_name ? (
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-slate-400" />
                        {rule.part_type_name}
                      </div>
                    ) : (
                      <span className="text-slate-400">All</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {rule.region ? (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {rule.region}
                      </div>
                    ) : (
                      <span className="text-slate-400">All</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {rule.estimated_share_percent != null 
                      ? `${rule.estimated_share_percent}%` 
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    {getConfidenceBadge(rule.confidence_level)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(rule)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => deleteMutation.mutate(rule.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || !!editingRule} onOpenChange={(open) => {
        if (!open) handleCloseDialog();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit Segment Rule' : 'Add Segment Rule'}
            </DialogTitle>
            <DialogDescription>
              {editingRule 
                ? 'Update segment rule'
                : 'Assign a competitor to an OEM/Part Type/Region combination'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="competitor_id">Competitor *</Label>
              <Select 
                value={formData.competitor_id} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, competitor_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select competitor" />
                </SelectTrigger>
                <SelectContent>
                  {competitors.map((comp) => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.name} {comp.short_name && `(${comp.short_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="oem">OEM *</Label>
              <Select 
                value={formData.oem} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, oem: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select OEM" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {oems.map((oem) => (
                    <SelectItem key={oem} value={oem}>
                      {oem}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="part_type_id">Part Type</Label>
                <Select 
                  value={formData.part_type_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, part_type_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All part types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All part types</SelectItem>
                    {partTypes.map((pt) => (
                      <SelectItem key={pt.id} value={pt.id}>
                        {pt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="region">Region</Label>
                <Select 
                  value={formData.region} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All regions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All regions</SelectItem>
                    {regions.map((region) => (
                      <SelectItem key={region} value={region}>
                        {region}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="estimated_share_percent">Estimated Share (%)</Label>
                <Input
                  id="estimated_share_percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.estimated_share_percent}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_share_percent: e.target.value }))}
                  placeholder="45"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confidence_level">Confidence Level</Label>
                <Select 
                  value={formData.confidence_level} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, confidence_level: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="source_notes">Source / Notes</Label>
              <Textarea
                id="source_notes"
                value={formData.source_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, source_notes: e.target.value }))}
                placeholder="e.g., Industry report 2024, Customer intel..."
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending || !formData.competitor_id || !formData.oem}>
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingRule ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentRuleManagement;
